// Copyright (c) 2021, 37.78 Tecnologia Ltda. All rights reserved.

import React, { useState } from "react";

import {
  Box,
  Divider,
  Drawer,
  Grid,
  Link as GobanLink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@3778.care/goban";
import PersonIcon from "@mui/icons-material/Person";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";

import { ConfirmationDialog } from "../../components/index";
import { useAuth } from "../auth";
import { useMixpanel } from "../mixpanel";
import {
  ItemGrid,
  NameGrid,
  OrganizationBox,
  OrganizationGrid,
  ProfileChip,
  ProfileGrid,
  StyledAvatar,
  StyledHelpIcon,
  StyledListItem,
  StyledLockIcon,
  StyledMailIcon,
  StyledPaper,
  StyledSubHeader,
  StyledTransitIcon,
  StyledWorkIcon,
} from "./styles.js";

export const ProfileDrawer = ({ patient, state, toggleDrawer }) => {
  const { logout, isGoogleLogin } = useAuth();
  const [isOpen, setOpen] = useState(false);
  const navigate = useNavigate();
  const { track } = useMixpanel();

  const toggleDialog = () => setOpen(!isOpen);

  const handleLogout = async () => {
    await logout();

    setTimeout(() => {
      track("Logout - ProfileDrawer");
      navigate("/user/login");
    }, 0);
  };

  const renderSubHeader = (item) => (
    <StyledSubHeader component="div">{item}</StyledSubHeader>
  );

  return (
    <>
      <ConfirmationDialog
        cancelText="Não, desejo continuar"
        confirmText="Sim"
        isOpen={isOpen}
        onConfirm={handleLogout}
        onToggle={toggleDialog}
        title="Deseja realmente sair?"
      />
      <Drawer anchor="right" open={state} onClose={toggleDrawer}>
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
          justifyContent="space-between"
          onClick={toggleDrawer}
          width="305px"
        >
          <Box>
            <StyledPaper square elevation={0}>
              <Grid container spacing={0}>
                <ItemGrid item xs={12}>
                  <StyledAvatar data-cy="profile-avatar">
                    <PersonIcon fontSize="large" />
                  </StyledAvatar>
                </ItemGrid>
                <NameGrid item xs={12}>
                  <Typography variant="subtitle2" align="center">
                    {patient?.fullName}
                  </Typography>
                </NameGrid>
                <OrganizationGrid item xs={12}>
                  <Box alignItems="center" display="flex">
                    <OrganizationBox>
                      {patient?.organization && (
                        <StyledWorkIcon color="action" fontSize="small" />
                      )}
                    </OrganizationBox>
                    <Typography color="textSecondary" variant="body2">
                      {patient?.organization}
                    </Typography>
                  </Box>
                </OrganizationGrid>
                <ProfileGrid item xs={12}>
                  <ProfileChip
                    clickable
                    component={Link}
                    label="Editar dados pessoais"
                    to="/user/profile"
                    variant="outlined"
                  />
                </ProfileGrid>
              </Grid>
            </StyledPaper>
            {isGoogleLogin ===
              "TODO: remove this comparison when edit email and password is ready" && (
              <>
                <List
                  data-cy="account-list"
                  subheader={renderSubHeader("Conta")}
                >
                  <ListItem button component={Link} to="#">
                    <ListItemIcon>
                      <StyledMailIcon />
                    </ListItemIcon>
                    <ListItemText primary="Alterar e-mail" />
                  </ListItem>

                  <ListItem button component={Link} to="#">
                    <ListItemIcon>
                      <StyledLockIcon />
                    </ListItemIcon>
                    <ListItemText primary="Alterar senha" />
                  </ListItem>
                </List>
                <Divider />
              </>
            )}
            <List data-cy="support-list" subheader={renderSubHeader("Suporte")}>
              <ListItem
                button
                color="inherit"
                component={GobanLink}
                href="https://api.whatsapp.com/send?phone=553135284148&text=Ol%C3%A1!%20Estou%20com%20problemas%20para%20usar%20o%20site"
                target="_blank"
              >
                <ListItemIcon>
                  <StyledHelpIcon />
                </ListItemIcon>
                <ListItemText primary="Whatsapp" />
              </ListItem>
            </List>
          </Box>

          <Box>
            <Divider />
            <StyledListItem button onClick={toggleDialog}>
              <ListItemIcon>
                <StyledTransitIcon />
              </ListItemIcon>
              <ListItemText primary="Sair da conta" />
            </StyledListItem>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

ProfileDrawer.propTypes = {
  patient: PropTypes.object,
  state: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func,
};
